import axios from 'axios';
import Swal from 'sweetalert2';

const REQUEST_TIMEOUT_MS = 60000;

// Crear instancia de axios
const apiClient = axios.create({
    baseURL: process.env.VUE_APP_API_URL || 'http://localhost:8000/api',
    timeout: REQUEST_TIMEOUT_MS,
    headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
    },
});

// axios usa ECONNABORTED tanto para timeout como para abort; el mensaje los distingue
const isTimeoutError = (error) => {
    return error.code === 'ETIMEDOUT'
        || (error.code === 'ECONNABORTED' && /timeout/i.test(error.message || ''));
};

const showTimeoutModal = (error) => {
    const occurredAt = new Date();
    const timeLabel = occurredAt.toLocaleString('es-ES', { hour12: false });
    const method = (error.config?.method || 'get').toUpperCase();
    const seconds = Math.round(REQUEST_TIMEOUT_MS / 1000);

    console.error(`[apiClient] timeout tras ${seconds}s`, {
        at: occurredAt.toISOString(),
        method,
        url: error.config?.url,
    });

    let htmlContent = `<p>La conexión con SAP (Idilia) no ha respondido en ${seconds} segundos y la petición se ha cancelado.</p>`;

    if (method !== 'GET') {
        htmlContent += '<p>Si estabas realizando una acción sobre un pedido, comprueba su estado antes de repetirla: puede haberse completado en SAP.</p>';
    }

    htmlContent += `
        <div style="margin-top: 15px; text-align: left; font-size: 0.9em; color: #666;">
            <p style="margin-bottom: 5px;">Hora del incidente: <strong>${timeLabel}</strong></p>
            <p style="margin-bottom: 0;">Si el problema se repite, indica esta hora a soporte.</p>
        </div>`;

    Swal.fire({
        icon: 'warning',
        title: 'SAP está tardando más de lo normal',
        html: htmlContent,
        confirmButtonColor: '#f6a700',
        confirmButtonText: 'Entendido'
    });
};

const isIdiliaError = (error) => {
    const data = error.response?.data;
    if (!data) return false;

    return data.error_source === 'idilia';
};

const showIdiliaErrorModal = (errorMessage = null) => {
    let htmlContent = '<p>No se pudo conectar con el sistema SAP de Idilia. Por favor, inténtalo de nuevo más tarde o contacta con soporte técnico.</p>';

    if (errorMessage) {
        const escapedMessage = errorMessage
            .replace(/&/g, '&amp;')
            .replace(/</g, '&lt;')
            .replace(/>/g, '&gt;');
        htmlContent += `
            <div style="margin-top: 15px; text-align: left;">
                <p style="margin-bottom: 5px; font-size: 0.9em; color: #666;">Detalles del error:</p>
                <pre style="background: #f4f4f4; padding: 10px; border-radius: 5px; font-size: 0.85em; overflow-x: auto; white-space: pre-wrap; word-break: break-word; text-align: left; max-height: 200px; overflow-y: auto;"><code>${escapedMessage}</code></pre>
            </div>`;
    }

    Swal.fire({
        icon: 'error',
        title: 'Error de conexión',
        html: htmlContent,
        confirmButtonColor: '#f6a700',
        confirmButtonText: 'Entendido'
    });
};

// Request interceptor para agregar token
apiClient.interceptors.request.use(
    (config) => {
        const user = JSON.parse(localStorage.getItem('user'));
        if (user && user.token) {
            config.headers.Authorization = `Bearer ${user.token}`;
        }
        return config;
    },
    (error) => {
        console.error('[apiClient] error en request interceptor:', error);
        return Promise.reject(error);
    }
);

// Response interceptor para manejar errores
apiClient.interceptors.response.use(
    (response) => {
        return response;
    },
    (error) => {
        if (error.response && error.response.status === 401) {
            // Token inválido o expirado - logout automático
            localStorage.removeItem('user');
            window.location.href = '/login';
        }

        if (isIdiliaError(error)) {
            const errorMessage = error.response?.data?.messages?.[0]?.message || null;
            showIdiliaErrorModal(errorMessage);
        }

        if (isTimeoutError(error)) {
            error.message = `SAP no ha respondido en ${Math.round(REQUEST_TIMEOUT_MS / 1000)} segundos`;
            showTimeoutModal(error);
        }

        return Promise.reject(error);
    }
);

export default apiClient;
